/**
 * Video Slider Control
 * Ensures only one video plays at a time in #video-slider
 * Pauses all videos when switching between slides
 * Handles both native HTML5 videos and Wistia iframe embeds
 */
( function() {
  'use strict';
  
  try {
    document.addEventListener( 'DOMContentLoaded', function() {
      try {
        // Early return if #video-slider doesn't exist
        const videoSlider = document.querySelector( '#video-slider' );
        if ( !videoSlider ) {
          return;
        }

        // Store Wistia video handles and their corresponding iframes
        const wistiaVideos = [];
        const wistiaIframeMap = new Map(); // Maps iframe element to Wistia video handle
        const nativeVideos = [];

        // Find all Wistia iframes within the video slider
        const getAllWistiaIframes = function() {
          const iframes = videoSlider.querySelectorAll( 'iframe[src*="wistia"]' );
          return iframes;
        };

        // Find all native videos within the video slider
        const getAllNativeVideos = function() {
          const videos = videoSlider.querySelectorAll( 'video' );
          return videos;
        };

        // Pause Wistia video via postMessage (fallback when API not available)
        const pauseWistiaIframe = function( iframe ) {
          try {
            const message = JSON.stringify( { method: 'pause', value: true } );
            iframe.contentWindow.postMessage( message, '*' );
            return true;
          } catch ( e ) {
            return false;
          }
        };

        // Get Wistia video handle from iframe
        const getWistiaHandle = function( iframe ) {
          if ( typeof window.Wistia === 'undefined' ) {
            return null;
          }
    
          try {
            // Extract video ID from iframe src
            const src = iframe.src || iframe.getAttribute( 'src' );
            const match = src.match( /iframe\/([a-z0-9]+)/i );
            if ( match && match[1] ) {
              const videoId = match[1];
              const video = window.Wistia.api( videoId );
              if ( video ) {
                return video;
              }
            }
          } catch ( e ) {
            // Silent fail
          }
          return null;
        };

        // Pause all videos (both native and Wistia) except the one specified
        const pauseAllVideosExcept = function( activeVideo, activeWistiaVideo ) {
          // Pause native videos
          nativeVideos.forEach( function( video ) {
            if ( video !== activeVideo && !video.paused ) {
              video.pause();
            }
          } );
    
          // Pause Wistia videos via API (if available)
          wistiaVideos.forEach( function( wistiaVideo ) {
            try {
              if ( wistiaVideo !== activeWistiaVideo && wistiaVideo.state && wistiaVideo.state() !== 'paused' ) {
                wistiaVideo.pause();
              }
            } catch ( e ) {
              // Silent fail
            }
          } );
    
          // Also pause all Wistia iframes directly via postMessage (except active one)
          const allWistiaIframes = getAllWistiaIframes();
          allWistiaIframes.forEach( function( iframe ) {
            // Skip if this iframe corresponds to the active Wistia video
            const iframeVideo = wistiaIframeMap.get( iframe );
            if ( iframeVideo === activeWistiaVideo ) {
              return; // Skip active video
            }
      
            // Pause via postMessage if API not available or as fallback
            if ( typeof window.Wistia === 'undefined' || !iframeVideo ) {
              pauseWistiaIframe( iframe );
            }
          } );
        };

        // Pause all videos (both native and Wistia)
        const pauseAllVideos = function() {
          // Pause native videos
          nativeVideos.forEach( function( video ) {
            if ( !video.paused ) {
              video.pause();
            }
          } );
    
          // Pause all Wistia iframes - use postMessage for all (works even without API)
          // This ensures all videos are paused regardless of API availability
          const allWistiaIframes = getAllWistiaIframes();
          allWistiaIframes.forEach( function( iframe ) {
            // Try API first if available and video is registered
            const iframeVideo = wistiaIframeMap.get( iframe );
            if ( iframeVideo && iframeVideo.pause && typeof window.Wistia !== 'undefined' ) {
              try {
                if ( iframeVideo.state && iframeVideo.state() !== 'paused' ) {
                  iframeVideo.pause();
                }
              } catch ( e ) {
                // Silent fail, fall through to postMessage
              }
            }
            // Always use postMessage to ensure pause (works even if API fails or isn't loaded)
            pauseWistiaIframe( iframe );
          } );
        };

        // Initialize Wistia video listeners
        const initWistiaListeners = function() {
          const iframes = getAllWistiaIframes();
    
          iframes.forEach( function( iframe ) {
            // Wait for Wistia API to be ready
            const tryGetHandle = function( attempts ) {
              attempts = attempts || 0;
              const wistiaVideo = getWistiaHandle( iframe );
        
              if ( wistiaVideo ) {
                wistiaVideos.push( wistiaVideo );
                wistiaIframeMap.set( iframe, wistiaVideo ); // Map iframe to video handle
          
                // Listen for play event
                wistiaVideo.bind( 'play', function() {
                  pauseAllVideosExcept( null, wistiaVideo );
                } );
          
                // Listen for pause event
                wistiaVideo.bind( 'pause', function() {
                  // Video paused
                } );
              } else if ( attempts < 10 ) {
                // Retry if Wistia API not ready yet
                setTimeout( function() {
                  tryGetHandle( attempts + 1 );
                }, 500 );
              }
            };
      
            tryGetHandle();
          } );
        };

        // Initialize native video event listeners
        const initNativeVideoListeners = function() {
          const videos = getAllNativeVideos();
    
          videos.forEach( function( video ) {
            if ( nativeVideos.indexOf( video ) === -1 ) {
              nativeVideos.push( video );
            }
      
            // When a video starts playing, pause all others
            video.addEventListener( 'play', function() {
              pauseAllVideosExcept( video, null );
            } );

            // Also handle when user clicks play button
            video.addEventListener( 'playing', function() {
              pauseAllVideosExcept( video, null );
            } );
          } );
        };

        // Initialize all video listeners
        const initVideoListeners = function() {
          initNativeVideoListeners();
          initWistiaListeners();
        };

        // Handle Swiper slide changes
        const handleSwiperSlideChange = function() {
          let swiperAttached = false;
    
          const attachSwiperListeners = function() {
            const swiperContainer = videoSlider.querySelector( '.swiper' );
      
            if ( swiperContainer && swiperContainer.swiper && !swiperAttached ) {
              const swiper = swiperContainer.swiper;
        
              // Pause all videos when slide changes
              swiper.on( 'slideChange', function() {
                pauseAllVideos();
              } );

              // Also pause on transition start
              swiper.on( 'slideChangeTransitionStart', function() {
                pauseAllVideos();
              } );
        
              swiperAttached = true;
              return true;
            }
            return false;
          };

          // Try to attach immediately
          if ( attachSwiperListeners() ) {
            return;
          }

          // If Swiper isn't initialized yet, wait for it
          // Use MutationObserver to detect when Swiper is initialized
          const observer = new MutationObserver( function() {
            if ( attachSwiperListeners() ) {
              observer.disconnect();
            }
          } );

          observer.observe( videoSlider, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class']
          } );

          // Also try to find it after delays (Swiper might initialize asynchronously)
          [500, 1000, 2000, 3000].forEach( function( delay ) {
            setTimeout( function() {
              if ( attachSwiperListeners() ) {
                observer.disconnect();
              }
            }, delay );
          } );

          // Also listen for navigation button clicks as fallback
          const navButtons = videoSlider.querySelectorAll( '.swiper-button-next, .swiper-button-prev' );
          navButtons.forEach( function( button ) {
            button.addEventListener( 'click', function() {
              // Small delay to ensure slide change has happened
              setTimeout( function() {
                pauseAllVideos();
              }, 100 );
            } );
          } );

          // Listen for pagination clicks
          const pagination = videoSlider.querySelectorAll( '.swiper-pagination-bullet' );
          pagination.forEach( function( bullet ) {
            bullet.addEventListener( 'click', function() {
              setTimeout( function() {
                pauseAllVideos();
              }, 100 );
            } );
          } );
        };

        // Initialize everything
        initVideoListeners();
        handleSwiperSlideChange();

        // Re-initialize if new videos are added dynamically
        const observer = new MutationObserver( function( mutations ) {
          mutations.forEach( function( mutation ) {
            if ( mutation.addedNodes.length > 0 ) {
              // Check if any added nodes are videos, iframes, or contain videos/iframes
              let shouldReinit = false;
              mutation.addedNodes.forEach( function( node ) {
                if ( node.nodeType === 1 ) { // Element node
                  if ( node.tagName === 'VIDEO' || 
                node.tagName === 'IFRAME' || 
                node.querySelector( 'video' ) || 
                node.querySelector( 'iframe[src*="wistia"]' ) ) {
                    shouldReinit = true;
                  }
                }
              } );
              if ( shouldReinit ) {
                // Clear arrays and reinitialize
                wistiaVideos.length = 0;
                nativeVideos.length = 0;
                initVideoListeners();
              }
            }
          } );
        } );

        observer.observe( videoSlider, {
          childList: true,
          subtree: true
        } );
  
        // Also wait for Wistia API to load if not already loaded
        if ( typeof window.Wistia === 'undefined' ) {
          const checkWistia = setInterval( function() {
            if ( typeof window.Wistia !== 'undefined' ) {
              clearInterval( checkWistia );
              initWistiaListeners();
            }
          }, 500 );
    
          // Stop checking after 10 seconds
          setTimeout( function() {
            clearInterval( checkWistia );
          }, 10000 );
        }
      } catch ( error ) {
        // Silent fail
      }
    } ); // End DOMContentLoaded
  } catch ( error ) {
    // Silent fail
  }
} )(); // End IIFE
